import React, { useState, useCallback, useEffect } from 'react';
import {
  useApi,
  microsoftAuthApiRef,
  SignInPageProps,
} from '@backstage/core-plugin-api';
import { UserIdentity } from '@backstage/core-components';

const CAT_YELLOW = '#ffcd11';
const CAT_BLACK = '#000000';
const CAT_DARK = '#1a1a1a';
const CAT_GRAY = '#3f3f3f';
const CAT_LIGHT_GRAY = '#f8f8f8';
const CAT_BORDER = '#e0e0e0';
const CAT_SUBTLE = '#777777';
const CAT_BLUE = '#0078d4';

const SvgDatabase = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <ellipse cx="12" cy="5" rx="9" ry="3" />
    <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
    <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
  </svg>
);

const SvgGear = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

const SvgSearch = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const SvgShield = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const SvgUserPlus = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <line x1="23" y1="11" x2="17" y2="11" />
    <line x1="20" y1="8" x2="20" y2="14" />
  </svg>
);

const SvgLink = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

const SvgActivity = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
  </svg>
);

const SvgNetwork = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="5" r="3" />
    <circle cx="5" cy="19" r="3" />
    <circle cx="19" cy="19" r="3" />
    <line x1="12" y1="8" x2="5.5" y2="16" />
    <line x1="12" y1="8" x2="18.5" y2="16" />
  </svg>
);

const features: { icon: React.ReactNode; title: string; description: string }[] = [
  {
    icon: <SvgDatabase />,
    title: 'Centralizes Service Metadata',
    description: 'One place for ownership, lifecycle, and dependency info.'
  },
  {
    icon: <SvgGear />,
    title: 'Automation-Friendly',
    description: 'Structured data ready for tools and pipelines to consume.'
  },
  {
    icon: <SvgSearch />,
    title: 'Enhanced Service Discovery',
    description: 'Quickly find and understand services across teams.'
  },
  {
    icon: <SvgShield />,
    title: 'Improved Governance',
    description: 'Enforce policies and track ownership across services.'
  },
  {
    icon: <SvgUserPlus />,
    title: 'Easier Maintenance & Onboarding',
    description: 'Onboard faster with clear docs and ownership details.'
  },
  {
    icon: <SvgLink />,
    title: 'Seamless Integration',
    description: 'Connects with Backstage, CI/CD pipelines, and monitoring tools.'
  },
  {
    icon: <SvgNetwork />,
    title: 'Dependency Mapping',
    description: 'Visualize relationships across your entire architecture.'
  },
  {
    icon: <SvgActivity />,
    title: 'Health Monitoring',
    description: 'Real-time visibility into service health and uptime.'
  },
];


export const SignInPage = ({ onSignInSuccess }: SignInPageProps) => {
  const microsoftAuth = useApi(microsoftAuthApiRef);
  const [loading, setLoading] = useState(false);
  const [silentLoading, setSilentLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hover, setHover] = useState(false);

  // On mount, attempt a silent session restore so reloads don't require
  // the user to click Sign In again when their SSO session is still active.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await microsoftAuth.getBackstageIdentity({
          optional: true,
        });
        if (cancelled) return;
        if (response) {
          const profile = await microsoftAuth.getProfile({ optional: true });
          if (!cancelled) {
            onSignInSuccess(
              UserIdentity.create({
                identity: response.identity,
                authApi: microsoftAuth,
                profile,
              }),
            );
          }
        }
      } catch {
        // Silent restore failed — fall through to show the sign-in UI.
      } finally {
        if (!cancelled) setSilentLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [microsoftAuth, onSignInSuccess]);

  const handleSignIn = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await microsoftAuth.getBackstageIdentity({
        instantPopup: true,
      });
      if (!response) {
        throw new Error('No identity returned. Please try again.');
      }
      const profile = await microsoftAuth.getProfile();
      onSignInSuccess(
        UserIdentity.create({
          identity: response.identity,
          authApi: microsoftAuth,
          profile,
        }),
      );
    } catch (err: any) {
      setError(err?.message ?? 'Sign in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [microsoftAuth, onSignInSuccess]);

  // Don't flash the sign-in page while we attempt silent restore on reload.
  if (silentLoading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          backgroundColor: '#f8f8f8',
          fontFamily: "'Helvetica Neue', Arial, sans-serif",
        }}
      >
        <span
          style={{
            display: 'inline-block',
            width: '28px',
            height: '28px',
            border: '3px solid #e0e0e0',
            borderTopColor: '#1a1a1a',
            borderRadius: '50%',
            animation: 'cat-spin 0.8s linear infinite',
          }}
        />
        <style>{`@keyframes cat-spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        backgroundColor: CAT_LIGHT_GRAY,
        fontFamily: "'Helvetica Neue', Arial, sans-serif",
      }}
    >
      {/* ── Header ─────────────────────────────────────────── */}
      <header
        style={{
          backgroundColor: CAT_BLACK,
          padding: '0 40px',
          height: '65px',
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <img src="/logo.png" alt="CAT Logo" style={{ height: '28px', marginRight: '10px' }} />
        <span
          style={{
            color: CAT_YELLOW,
            fontWeight: 800,
            fontSize: '24px',
            letterSpacing: '2.5px',
            textTransform: 'uppercase',
          }}
        >
          Service Catalog
        </span>
      </header>

      {/* ── Main ───────────────────────────────────────────── */}
      <main
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 24px',
        }}
      >
        {/* Centered container */}
        <div
          style={{
            width: '100%',
            maxWidth: '1080px',
            display: 'flex',
            backgroundColor: '#ffffff',
            border: `1px solid ${CAT_BORDER}`,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          }}
        >
          {/* ── Left: Sign-In panel ── */}
          <div
            className="cat-left-panel"
            style={{
              flex: '0 0 420px',
              borderRight: `1px solid ${CAT_BORDER}`,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            {/* Card title */}
            <div style={{ padding: '52px 44px 28px' }}>
              <h1
                style={{
                  margin: '0 0 6px',
                  fontSize: '28px',
                  fontWeight: 800,
                  textAlign: 'center',
                  color: CAT_DARK,
                  letterSpacing: '-0.3px',
                }}
              >
                Sign In
              </h1>
              <p
                style={{
                  margin: 0,
                  textAlign: 'center',
                  color: '#6b6b6b',
                  fontSize: '14px',
                  fontWeight: 400,
                }}
              >
               to manage your services
              </p>
            </div>

            <hr style={{ border: 'none', borderTop: `1px solid ${CAT_BORDER}`, margin: '0 0 0 0' }} />

            {/* Card body */}
            <div style={{ padding: '36px 44px 52px' }}>
              {error && (
                <div
                  style={{
                    backgroundColor: '#fff3f3',
                    border: '1px solid #f5c6cb',
                    color: '#c62828',
                    padding: '10px 14px',
                    marginBottom: '20px',
                    fontSize: '13px',
                    lineHeight: 1.4,
                  }}
                >
                  {error}
                </div>
              )}

              <button
                onClick={handleSignIn}
                disabled={loading}
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                style={{
                  width: '100%',
                  backgroundColor: loading ? '#d4a900' : hover ? '#e8c000' : CAT_YELLOW,
                  color: CAT_DARK,
                  border: 'none',
                  padding: '13px 24px',
                  fontSize: '15px',
                  fontWeight: 600,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  letterSpacing: '0.3px',
                  transition: 'background-color 0.15s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  opacity: loading ? 0.8 : 1,
                }}
              >
                {loading ? (
                  <>
                    <span
                      style={{
                        display: 'inline-block',
                        width: '14px',
                        height: '14px',
                        border: '2px solid #888',
                        borderTopColor: CAT_DARK,
                        borderRadius: '50%',
                        animation: 'cat-spin 0.8s linear infinite',
                      }}
                    />
                    Signing in…
                  </>
                ) : (
                  'Sign in with CAT SSO'
                )}
              </button>

              <p
                style={{
                  textAlign: 'center',
                  marginTop: '20px',
                  fontSize: '12px',
                  color: CAT_SUBTLE,
                }}
              >
                Powered by Microsoft Azure Active Directory
              </p>
              {/* <span style={{ display: 'block', marginTop: '8px', textAlign: 'center' }}>
              <p
                style={{
                  textAlign: 'center',
                  marginTop: '14px',
                  fontSize: '12px',
                  color: CAT_SUBTLE,
                }}
                >
                Need access? <br/><a style={{ color: CAT_BLUE }} href="mailto:Service_Adoption_Team_Cosmos@cat.com">Raise request in Devops Portal.</a>
              </p>
              </span> */}
            </div>
          </div>

          {/* ── Right: Feature grid ── */}
          <div
            className="cat-right-panel"
            style={{
              flex: 1,
              padding: '48px 52px',
            }}
          >
            <h2
              style={{
                margin: '0 0 10px',
                fontSize: '20px',
                fontWeight: 700,
                color: CAT_DARK,
                letterSpacing: '-0.2px',
              }}
            >
              One Catalog. All of Helios.
            </h2>
            <p
              style={{
                margin: '0 0 32px',
                fontSize: '13px',
                color: CAT_GRAY,
                lineHeight: 1.65,
              }}
            >
              Access the full suite of Helios Platform services with your account. Discover
              APIs, manage service metadata, track dependencies, and more.
            </p>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '20px 32px',
              }}
            >
              {features.map(feature => (
                <div
                  key={feature.title}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                  }}
                >
                  <div
                    style={{
                      width: '44px',
                      height: '44px',
                      borderRadius: '50%',
                      backgroundColor: '#1a1a1a',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {feature.icon}
                  </div>
                  <div>
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: '13px',
                        color: CAT_DARK,
                        marginBottom: '4px',
                      }}
                    >
                      {feature.title}
                    </div>
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#5a5a5a',
                        lineHeight: 1.55,
                      }}
                    >
                      {feature.description}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>

      {/* ── Footer ─────────────────────────────────────────── */}
      <footer
        style={{
          borderTop: `1px solid ${CAT_BORDER}`,
          padding: '14px 40px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
          backgroundColor: CAT_LIGHT_GRAY,
        }}
      >
        <div style={{ fontSize: '12px', color: CAT_SUBTLE }}>
          Caterpillar &copy; 2026. All Rights Reserved
        </div>
        <div style={{ display: 'flex', gap: '16px', fontSize: '12px' }}>
          {[
            { label: 'Privacy', href: 'https://privacyportal.onetrust.com/webform/ec73b9a1-eecd-48d1-a789-fa7761e54a29/cf3dfa36-1b53-4ec9-8dfc-e84988613fa1' },
            { label: 'Legal', href: 'https://www.caterpillar.com/en/legal-notices.html' },
          ].map(({ label, href }, i, arr) => (
            <React.Fragment key={label}>
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: CAT_SUBTLE, textDecoration: 'none' }}
                onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.textDecoration = 'underline'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.textDecoration = 'none'; }}
              >
                {label}
              </a>
              {i < arr.length - 1 && <span style={{ color: CAT_BORDER }}>|</span>}
            </React.Fragment>
          ))}
        </div>
      </footer>

      {/* Spinner keyframe + responsive */}
      <style>{`
        @keyframes cat-spin {
          to { transform: rotate(360deg); }
        }
        @media (max-width: 860px) {
          .cat-right-panel {
            display: none !important;
          }
          .cat-left-panel {
            flex: 1 !important;
            border-right: none !important;
          }
        }
      `}</style>
    </div>
  );
};
